import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreateVendorInput,
  SearchVendorCandidatesInput,
  UpdateVendorInput,
  VendorTrade,
} from '@/models/Vendor';

import {
  autoPopulateAllTrades,
  autoPopulateVendors,
  createVendor,
  deleteVendor,
  getPropertyVendors,
  searchVendorCandidates,
  updateVendor,
} from '@/services/api/vendors';

export const useVendors = (propertyId?: string) => {
  const queryClient = useQueryClient();

  const { data: vendors, isFetching: isFetchingVendors } = useQuery({
    queryKey: ['vendors', propertyId],
    queryFn: () => getPropertyVendors(propertyId),
    enabled: Boolean(propertyId),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['vendors', propertyId] });

  const { mutateAsync: createMutate, isPending: isCreatingVendor } = useMutation({
    mutationFn: createVendor,
    onSuccess: invalidate,
  });

  const { mutateAsync: updateMutate, isPending: isUpdatingVendor } = useMutation({
    mutationFn: updateVendor,
    onSuccess: invalidate,
  });

  const { mutateAsync: deleteMutate, isPending: isDeletingVendor } = useMutation({
    mutationFn: deleteVendor,
    onSuccess: invalidate,
  });

  const { mutateAsync: autoPopulateMutate, isPending: isAutoPopulating } = useMutation({
    mutationFn: autoPopulateVendors,
    onSuccess: invalidate,
  });

  const { mutateAsync: autoPopulateAllMutate, isPending: isAutoPopulatingAll } = useMutation({
    mutationFn: autoPopulateAllTrades,
    onSuccess: invalidate,
  });

  return {
    vendors: vendors ?? [],
    isFetchingVendors,

    createVendor: (data: CreateVendorInput) =>
      propertyId ? createMutate({ propertyId, data }) : Promise.resolve(undefined),
    isCreatingVendor,

    updateVendor: (id: string, data: UpdateVendorInput) =>
      propertyId ? updateMutate({ id, propertyId, data }) : Promise.resolve(undefined),
    isUpdatingVendor,

    deleteVendor: (id: string) =>
      propertyId ? deleteMutate({ id, propertyId }) : Promise.resolve(undefined),
    isDeletingVendor,

    autoPopulate: (trade: VendorTrade, count = 3) =>
      propertyId ? autoPopulateMutate({ propertyId, trade, count }) : Promise.resolve([]),
    isAutoPopulating,

    autoPopulateAll: (count = 3) =>
      propertyId ? autoPopulateAllMutate({ propertyId, count }) : Promise.resolve([]),
    isAutoPopulatingAll,
  };
};

export const useVendorCandidates = (input: SearchVendorCandidatesInput | null) =>
  useQuery({
    queryKey: [
      'vendor-candidates',
      input?.propertyId,
      input?.trade,
      input?.textQuery,
      input?.filters,
    ],
    queryFn: () => {
      if (!input) throw new Error('useVendorCandidates: input required');
      return searchVendorCandidates(input);
    },
    // other_specialty requires a textQuery — block the call until the
    // user (eventually) types one, so we don't hit hauser-eq with an
    // unresolvable request.
    enabled: Boolean(
      input?.propertyId &&
        input?.trade &&
        (input.trade !== 'other_specialty' || (input.textQuery && input.textQuery.length > 0)),
    ),
  });
